/**
 * Felles oppslag fra plukklinje → paller (OrderSSCC).
 *
 * Brukes av både usePickingData (chips/teller + batch-badge) og ModernPickingDetails
 * (batch-banner), slik at de tre stedene ikke kan komme i utakt.
 *
 * Se «Claude md files/D-Pak-Pall-Nivaa3/Plan.md» (AP5).
 */

import { PalletSummaryDto, PalletItemDto, OrderBatchNoDto } from '../../../../types/picking';

/** Minimum som trengs for å identifisere en linje, uansett om den er PickingLine eller ExtendedPickingLine. */
export interface PalletLineKey {
  orderNo: number | string;
  /** Ordrelinjens LineNo. Kan mangle på eldre typer. */
  lineNo?: number;
  pListLineNo: number;
}

export interface PalletAllocation {
  sscc: string;
  quantity: number;
  itemBatchNo?: string;
  itemBatchExpDate?: string | null;
  gtin?: string;
}

/**
 * Finner pall-radene som hører til en plukklinje.
 *
 * `tolerantLineNoMatch` styrer hvor bredt vi matcher, og er **av som standard**:
 *
 * - **Av (alle eksisterende kunder):** nøyaktig samme predikat som før D-pak-arbeidet —
 *   `item.lineNo === line.pListLineNo`. Bit-identisk oppførsel, ingen regresjonsrisiko.
 * - **På (kun når auto-pall er aktivert):** matcher innenfor samme ordre mot BÅDE
 *   ordrelinjens LineNo og pListLineNo, fordi skrivesiden er inkonsistent — D-pak-flyten
 *   og createNewPallet skriver ordrelinjens LineNo (det eksporten joiner mot), mens
 *   handleUpdateQuantity og splitt skriver pListLineNo.
 *
 * Grunnen til at toleransen ikke er på for alle: `PListLineNo` og `LineNo` er ulike i stor
 * skala hos flere kunder (målt 2026-08-14: Norlux 39 168 av 130 787 linjer, PB Distribusjon
 * 220 661 av 445 841). Innenfor samme ordre kan da pListLineNo for én linje være lik LineNo
 * for en annen, slik at en pall ville vist seg på feil linje. Fjern skillet først når
 * skrivesiden er ryddet opp (se F2 i planen).
 */
export function getPalletAllocationsForLine(
  palletSummaries: PalletSummaryDto[],
  line: PalletLineKey,
  tolerantLineNoMatch: boolean = false
): PalletAllocation[] {
  const orderNo = Number(line.orderNo);

  const matches = (item: PalletItemDto): boolean => {
    if (!tolerantLineNoMatch) {
      return item.lineNo === line.pListLineNo; // uendret legacy-predikat
    }
    if (item.orderNo !== orderNo) return false;
    return item.lineNo === line.lineNo || item.lineNo === line.pListLineNo;
  };

  const allocations: PalletAllocation[] = [];

  for (const pallet of palletSummaries) {
    const items = pallet.items.filter(matches);
    if (items.length === 0) continue;

    const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
    if (quantity <= 0) continue;

    const withBatch = items.find(item => (item.itemBatchNo || '').trim().length > 0);

    allocations.push({
      sscc: pallet.sscc,
      quantity,
      itemBatchNo: withBatch?.itemBatchNo,
      itemBatchExpDate: withBatch?.itemBatchExpDate ?? null,
      gtin: items.find(item => item.gtin)?.gtin,
    });
  }

  return allocations;
}

/**
 * Antall distinkte batchnummer registrert på linja, på tvers av BEGGE kildene:
 * OrderBatchNo (manuell batch-registrering) og OrderSSCC.ItemBatchNo (D-pak-flyten,
 * og paller beriket manuelt). Uten OrderSSCC-delen står batch-varselet på selv når
 * batchen faktisk er fanget fra etiketten.
 */
export function countRegisteredBatches(
  palletSummaries: PalletSummaryDto[],
  line: PalletLineKey,
  orderBatchNumbers?: OrderBatchNoDto[],
  tolerantLineNoMatch: boolean = false
): number {
  const batches = new Set<string>();

  for (const row of orderBatchNumbers ?? []) {
    const value = (row.batchNo || '').trim();
    if (value) batches.add(value);
  }

  for (const allocation of getPalletAllocationsForLine(palletSummaries, line, tolerantLineNoMatch)) {
    const value = (allocation.itemBatchNo || '').trim();
    if (value) batches.add(value);
  }

  return batches.size;
}

/**
 * Antallet per pall når alle pallene på linja er like store (typisk D-pak: 12 × 35).
 * Returnerer null når pallene har ulikt antall, slik at kalleren kan droppe «à N».
 */
export function getUniformPalletQuantity(allocations: { quantity: number }[]): number | null {
  if (allocations.length === 0) return null;
  const first = allocations[0].quantity;
  return allocations.every(a => a.quantity === first) ? first : null;
}
